import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageWrapper from "../pageWrapper";

//CSS
import "../0PageStyles.css";

//Components
import Button from "../../Components/Other Components/button";
import PageLoader from "../../Components/Other Components/pageLoader";
import WarehousePicker from "../../Components/Warehouse Components/warehousePicker";
import ItemPicker from "../../Components/Item Components/itemPicker";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";

//Functions and hooks
import { TransferStock } from "../../BackendAccess/ItemHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import { usePopup } from "../../Contexts/PopupContext";

export default function TransferStockPage() {
  const [SourceWarehouse, setSourceWarehouse] = useState([]);
  const [DestinationWarehouse, setDestinationWarehouse] = useState([]);
  const [selectionItemAmounts, setSelectionItemAmounts] = useState(new Map());

  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);
  const [transferBtnDisabled, setTransferBtnDisabled] = useState(false);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const isNarrow = useNarrowMode();
  const navigate = useNavigate();
  const { t } = useTranslation("items");
  const { showPopup, removePopup } = usePopup();

  const sourceID = SourceWarehouse?.[0] || null;
  const destinationID = DestinationWarehouse?.[0] || null;

  const onSelectionItemAmountsChange = (amounts) => {
    setSelectionItemAmounts(amounts);
  };

  const onTransferClick = () => {
    if (!sourceID || !destinationID) {
      setError({
        err: t("Select a source and destination warehouse first."),
        errCode: 400,
      });
      return;
    }
    if (sourceID === destinationID) {
      setError({
        err: t("Source and destination warehouses must be different."),
        errCode: 400,
      });
      return;
    }
    const filteredItems = Array.from(selectionItemAmounts.entries()).filter(
      ([, amount]) => amount > 0
    );
    if (filteredItems.length === 0) {
      setError({
        err: t("Select at least one item to transfer."),
        errCode: 400,
      });
      return;
    }

    const key = Date.now();
    showPopup(
      <PopupConfirmation
        message={<p>{t("Are you sure you want to transfer this stock?")}</p>}
        onConfirm={() => onConfirmTransfer(key, filteredItems)}
        onCancel={() => removePopup(key)}
      />,
      key
    );
  };

  const onConfirmTransfer = async (key, filteredItems) => {
    setTransferBtnDisabled(true);
    showNotification(t("Transferring stock..."), "info");
    try {
      const items = filteredItems.map(([ItemID, Amount]) => ({
        ItemID,
        Amount,
      }));
      const [data, error] = await TransferStock(
        sourceID,
        destinationID,
        items
      );
      if (error) {
        setError(error);
        return;
      }
      showNotification(t("Stock transferred successfully!"), "success");
      navigate(-1);
    } finally {
      setTransferBtnDisabled(false);
      removePopup(key);
    }
  };

  const clickOnCancel = () => {
    navigate(-1);
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Transferring stock", { ns: "tutorial" }),
      message: t(
        "This page moves stock from one warehouse to another, in a single atomic step - the source warehouse's stock goes down and the destination's goes up together, or not at all.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "transfer-source-warehouse",
      title: t("Source Warehouse"),
      message: t(
        "Pick the warehouse you're moving stock out of.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "transfer-destination-warehouse",
      title: t("Destination Warehouse"),
      message: t(
        "Pick the warehouse you're moving stock into. It has to be different from the source warehouse.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".PageItemViewContainer",
      title: t("Select items to transfer"),
      message: t(
        "Once a source warehouse is picked, its items appear here. Use the + and - to set how much of each item to move - you can't move more than what's currently in the source warehouse.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "transfer-btn",
      title: t("Transfer", { ns: "items" }),
      message: t(
        "Once you've picked both warehouses and at least one item, click here to move the stock.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="transferStock" tutorialSteps={tutorialSteps}>
      <PageLoader loading={false} error={pageError}>
        <div className="page-content">
          <div
            className={
              isNarrow
                ? "PageWarehousePickerContainer mt-3 vertical"
                : "PageWarehousePickerContainer mt-3"
            }
          >
            <div
              data-tutorial-id="transfer-source-warehouse"
              className={isNarrow ? "mb-4 w-100" : "mr-4 w-100"}
            >
              <WarehousePicker
                title={t("Source Warehouse")}
                onChange={setSourceWarehouse}
                singleSelect={true}
                initWarehouses={[]}
              ></WarehousePicker>
            </div>
            <div data-tutorial-id="transfer-destination-warehouse" className="w-100">
              <WarehousePicker
                title={t("Destination Warehouse")}
                onChange={setDestinationWarehouse}
                singleSelect={true}
                initWarehouses={[]}
              ></WarehousePicker>
            </div>
          </div>
          <div className="PageItemViewContainer">
            <ItemPicker
              Title={t("Select items to transfer")}
              bShowAll={false}
              noteType="Delivery"
              WarehouseID={sourceID ? sourceID : -1}
              onSelectionItemAmountsChange={onSelectionItemAmountsChange}
              bDisabled={!sourceID}
              bLimitAmount={true}
              disabledMsg={t("Select a source warehouse first.")}
            />
          </div>
          <div className="btn-container">
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-positive"
              onClick={onTransferClick}
              disabled={transferBtnDisabled}
              acceptEnter={true}
              icon={"create"}
              tutorialId="transfer-btn"
            >
              {t("Transfer")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-primary"
              onClick={clickOnCancel}
            >
              {t("Cancel", { ns: "common" })}
            </Button>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
